<script lang="ts">
import { defineComponent } from 'vue'
import { format } from 'date-fns'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrDatePicker from '@nextrequestco/components/src/components/NrDatePicker'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import { mapActions } from 'vuex'

export default defineComponent({
  name: 'request-duedate',
  components: {
    NrIconButton,
    NrFormInput,
    NrIcon,
    NrTooltip,
    NrModal,
    NrDatePicker
  },
  props: {
    prettyId: { type: String },
    date: { type: String }
  },
  data() {
    return {
      showModal: false,
      updating: false,
      newDate: null,
      description: ''
    }
  },
  computed: {
    computedDate(): string | null {
      if (Date.parse(this.date)) {
        return format(new Date(this.date), 'MM/dd/yyyy')
      }
      return null
    },
    unselectableDates(): string[] {
      if (this.computedDate) {
        return [this.computedDate]
      } else {
        return []
      }
    }
  },
  methods: {
    ...mapActions('request_show', ['updateDuedate', 'fetchTimeline']),

    handleDateInput(date: Date) {
      this.newDate = date
    },

    async handleSaveData() {
      if (!this.newDate) return

      this.updating = true
      try {
        await this.updateDuedate({
          prettyId: this.prettyId,
          date: format(new Date(this.newDate), 'MM/dd/yyyy'),
          description: this.description
        })
        this.fetchTimeline({ pretty_id: this.prettyId })
        this.$toasted.success('Successfully updated request due date')
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        this.$toasted.error(
          'Request failed to update new due date. Please refresh the page and try again.'
        )
      }

      this.updating = false
      this.toggleModal(false)
    },

    toggleModal(state?: boolean) {
      this.showModal = state ?? !this.showModal
    }
  }
})
</script>

<template>
  <div class="panel-section p-bottom-8 request-due-date">
    <div
      class="panel-item is-flex is-align-items-center is-justify-content-space-between"
      :class="{ 'panel-item--editable': $can('publish', 'Request') }"
    >
      <p class="has-text-weight-semibold">Due</p>
      <NrTooltip
        label="Edit request due date"
        v-if="$can('publish', 'Request')"
      >
        <NrIconButton
          variant="minimal-neutral"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          aria-label="Edit request due date"
          @click="toggleModal(true)"
        />
      </NrTooltip>
    </div>
    <div class="m-left-8">
      <p>{{ date }}</p>
    </div>

    <NrModal
      title="Change due date"
      :show="showModal"
      :hasSecondaryButton="true"
      :disabeButtons="updating"
      secondaryButtonText="Cancel"
      primaryButtonText="Save date"
      @on-close-click="toggleModal(false)"
      @on-secondary-click="toggleModal(false)"
      @on-primary-click="handleSaveData"
    >
      <form @submit.prevent.stop>
        <fieldset>
          <NrDatePicker
            @input="handleDateInput"
            placeholder="Type or select a date (mm/dd/yyyy)"
            :modelValue="computedDate"
            :unselectable-dates="unselectableDates"
          >
            <template v-slot:label>New due date</template>
            <template v-slot:trigger><NrIcon name="calendar-alt" /></template>
          </NrDatePicker>
        </fieldset>
        <fieldset>
          <div class="field m-top-8">
            <NrFormInput
              id="description"
              label="Comments (optional)"
              rows="8"
              placeholder="Enter an optional reason for changing the due date. This message posts to timeline.  "
              tag="textarea"
              v-model="description"
            />
            <p class="help">
              Changing this date may have legal consquences. Please refer to
              appropriate laws.
            </p>
          </div>
        </fieldset>
      </form>
    </NrModal>
  </div>
</template>
